import { lazy } from 'react';
import { FadeInOut } from '@/components/FadeInOut';
import { CheckoutModal } from '@/features/billing/components/CheckoutModal';
import { LazyLoader } from '@/features/layouts/components/LazyLoader';
import { LayoutContainer } from '@/features/layouts/page/common/LayoutContainer';
import { DocumentContextProvider } from '@/features/printables/contexts/DocumentContext';
import { ExperienceTypes } from '@/types/gql.generated';
import { usePageExperienceContext } from '../../contexts/PageExperienceContext';
import { ErrorState } from '../ErrorState';
import { PageLayoutRenderer } from '../PageLayoutRenderer';

const PrintableLayout = lazy(
  () => import('@/features/layouts/page/PrintableLayout')
);
const PlannerLayout = lazy(
  () => import('@/features/planner/components/PlannerLayout')
);

export const PageExperience = () => {
  const { experience } = usePageExperienceContext();

  if (!experience) {
    return <ErrorState text="Missing experience data" />;
  }

  // Routing keys off the effective experience type. A layout preview sets that
  // type (and layoutId) via setPreview(getLayoutPreviewChanges(...)), so a
  // previewed printable layout renders through PrintableLayout here.
  let content;

  if (experience.type === ExperienceTypes.Printable) {
    content = <PrintableLayout />;
  } else if (experience.type === ExperienceTypes.Planner) {
    content = <PlannerLayout />;
  } else {
    content = (
      <DocumentContextProvider isPrintEnv={false}>
        <PageLayoutRenderer />
      </DocumentContextProvider>
    );
  }

  return (
    <LayoutContainer>
      <LazyLoader size="lg">
        {/* Keyed FadeInOut WITHOUT AnimatePresence: switching layouts (including
            applying/clearing a preview override) remounts this subtree so the
            outgoing layout unmounts immediately. A prior AnimatePresence
            mode="wait" kept the outgoing layout mounted through its exit, which
            re-rendered it with the reverted layoutId and could strand the
            incoming layout. display="block" so layouts fill the width instead
            of shrink-wrapping as row-flex items. initial="show" because lazy
            child chunks re-suspend this subtree and cancel an in-flight enter
            animation, stranding the page at opacity 0. */}
        <FadeInOut
          display="block"
          initial="show"
          key={String(experience?.layoutId)}
        >
          {content}
        </FadeInOut>
      </LazyLoader>
      <CheckoutModal />
    </LayoutContainer>
  );
};
